import React, { useState, useEffect } from 'react';
import { Layout, Menu, Typography, theme, ConfigProvider, Breadcrumb, Drawer, Button, Grid } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    TeamOutlined,
    BookOutlined,
    ThunderboltOutlined,
    SafetyOutlined,
    CrownOutlined,
    SettingOutlined,
    AppstoreOutlined,
    WarningOutlined,
    BugOutlined,
    MenuOutlined,
} from '@ant-design/icons';
import { useAuthStore } from '../../store/auth.store';

// Import extracted components
import { UsersTab } from './components/UsersTab';
import { RolesTab } from './components/RolesTab';
import { DictionariesTab } from './components/DictionariesTab';
import { FeatureFlagsTab } from './components/FeatureFlagsTab';
import { BusinessRulesTab } from './components/BusinessRulesTab';
import { ImportantTab } from './components/ImportantTab';
import { FeedbackTab } from './components/FeedbackTab';

import type { MenuProps } from 'antd';

const { Content, Sider } = Layout;
const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

type MenuItem = Required<MenuProps>['items'][number];

export default function AdminPage() {
    const screens = useBreakpoint();
    const isMobile = !screens.md;
    const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
    const { token } = theme.useToken();
    const { hasPermission } = useAuthStore();
    const location = useLocation();
    const navigate = useNavigate();
    
    // Determine initial tab based on path or permissions
    const getInitialTab = () => {
        const pathSegments = location.pathname.split('/').filter(Boolean);
        const path = pathSegments[pathSegments.length - 1];
        const validTabs = ['users', 'roles', 'dictionaries', 'rules', 'flags', 'important', 'feedback'];
        if (path && validTabs.includes(path)) return path;

        if (hasPermission('users:read')) return 'users';
        if (hasPermission('roles:read')) return 'roles';
        if (hasPermission('dictionaries:read')) return 'dictionaries';
        if (hasPermission('feedback:read') || hasPermission('feedback:manage')) return 'feedback';
        return 'users';
    };

    const [selectedKey, setSelectedKey] = useState(getInitialTab());

    // Update selected key when URL changes (e.g., from notification)
    useEffect(() => {
        const pathSegments = location.pathname.split('/').filter(Boolean);
        const path = pathSegments[pathSegments.length - 1];
        const validTabs = ['users', 'roles', 'dictionaries', 'rules', 'flags', 'important', 'feedback'];
        if (path && validTabs.includes(path) && path !== selectedKey) {
            setSelectedKey(path);
        }
    }, [location.pathname, selectedKey]);

    const handleTabChange = (key: string) => {
        setSelectedKey(key);
        navigate(`/admin/${key}${location.search}`);
        if (isMobile) setMobileMenuOpen(false);
    };

    const menuItems: MenuItem[] = [
        {
            key: 'group-users',
            label: 'Użytkownicy i Uprawnienia',
            type: 'group',
            children: [
                hasPermission('users:read') ? { key: 'users', icon: <TeamOutlined />, label: 'Użytkownicy' } : null,
                hasPermission('roles:read') ? { key: 'roles', icon: <CrownOutlined />, label: 'Role systemowe' } : null,
            ].filter(Boolean) as any
        },
        {
            key: 'group-system',
            label: 'Konfiguracja systemu',
            type: 'group',
            children: [
                hasPermission('dictionaries:read') ? { key: 'dictionaries', icon: <BookOutlined />, label: 'Słowniki' } : null,
                hasPermission('settings:read') ? { key: 'rules', icon: <SafetyOutlined />, label: 'Reguły biznesowe' } : null,
                hasPermission('settings:read') ? { key: 'flags', icon: <ThunderboltOutlined />, label: 'Moduły' } : null,
                hasPermission('settings:read') ? { key: 'important', icon: <WarningOutlined />, label: 'Ważne' } : null,
                (hasPermission('feedback:read') || hasPermission('feedback:manage')) ? { key: 'feedback', icon: <BugOutlined />, label: 'Zgłoszenia błędów' } : null,
            ].filter(Boolean) as any
        }
    ].filter(item => (item as any).children && (item as any).children.length > 0) as MenuItem[];

    const renderSelectedComponent = () => {
        switch (selectedKey) {
            case 'users': return <UsersTab />;
            case 'roles': return <RolesTab />;
            case 'dictionaries': return <DictionariesTab />;
            case 'flags': return <FeatureFlagsTab />;
            case 'rules': return <BusinessRulesTab />;
            case 'important': return <ImportantTab />;
            case 'feedback': return <FeedbackTab />;
            default: return <UsersTab />;
        }
    };

    const getSelectedLabel = () => {
        for (const group of menuItems as any[]) {
            if (group.children) {
                const found = group.children.find((child: any) => child.key === selectedKey);
                if (found) return found.label;
            }
        }
        return 'Panel administracyjny';
    };

    return (
        <ConfigProvider theme={{
            components: {
                Menu: {
                    itemBg: 'transparent',
                    itemSelectedBg: token.colorPrimaryBg,
                    itemSelectedColor: token.colorPrimary,
                    itemBorderRadius: 8,
                }
            }
        }}>
            <Layout style={{ minHeight: 'calc(100vh - 64px)', background: token.colorBgLayout, flexDirection: isMobile ? 'column' : 'row' }}>
                {!isMobile && (
                    <Sider
                        width={260}
                        style={{
                            background: token.colorBgContainer,
                            borderRight: `1px solid ${token.colorBorderSecondary}`,
                            paddingTop: 16
                        }}
                    >
                        <div style={{ padding: '0 24px 16px 24px', display: 'flex', alignItems: 'center', gap: 12 }}>
                            <div style={{ width: 40, height: 40, borderRadius: 8, background: token.colorPrimary, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <SettingOutlined style={{ color: 'white', fontSize: 20 }} />
                            </div>
                            <div>
                                <Title level={5} style={{ margin: 0 }}>Administracja</Title>
                                <Text type="secondary" style={{ fontSize: 12 }}>Zarządzanie systemem</Text>
                            </div>
                        </div>

                        <Menu
                            mode="inline"
                            selectedKeys={[selectedKey]}
                            onClick={(e) => handleTabChange(e.key)}
                            items={menuItems}
                            style={{ borderRight: 0, padding: '0 12px' }}
                        />
                    </Sider>
                )}

                {isMobile && (
                    <Drawer
                        title="Administracja"
                        placement="left"
                        onClose={() => setMobileMenuOpen(false)}
                        open={isMobileMenuOpen}
                        bodyStyle={{ padding: 0 }}
                    >
                        <Menu
                            mode="inline"
                            selectedKeys={[selectedKey]}
                            onClick={(e) => handleTabChange(e.key)}
                            items={menuItems}
                            style={{ borderRight: 0 }}
                        />
                    </Drawer>
                )}

                <Layout style={{ padding: isMobile ? '12px' : '24px 32px' }}>
                    <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Breadcrumb
                            items={[
                                { title: <><AppstoreOutlined /> System</> },
                                { title: 'Administracja' },
                                { title: getSelectedLabel() }
                            ]}
                            style={{ marginBottom: 0 }}
                        />
                        {isMobile && (
                            <Button 
                                icon={<MenuOutlined />} 
                                onClick={() => setMobileMenuOpen(true)}
                            >
                                Menu
                            </Button>
                        )}
                    </div>

                    <Content style={{
                        margin: 0,
                        minHeight: 280,
                    }}>
                        <div className="fade-in-slow">
                            {renderSelectedComponent()}
                        </div>
                    </Content>
                </Layout>
            </Layout>
        </ConfigProvider>
    );
}
